import React, { useState } from "react";
import {
  IconPrinter,
  IconDownload,
  IconCalendar,
  IconFileSpreadsheet,
  IconRotate,
  IconBox,
  IconUsers,
} from "@tabler/icons-react";
import { iconStroke } from "../../config/config";

export default function ReportsView({
  reports,
  summary,
  isLoading,
  filters,
  onFilterChange,
}) {
  const [reportType, setReportType] = useState(filters.type || "product");

  const handleTypeToggle = (type) => {
    setReportType(type);
    onFilterChange({ ...filters, type });
  };

  const handleDatePreset = (preset) => {
    const today = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    const fmt = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

    let fromStr = "";
    let toStr = fmt(today);

    if (preset === "today") {
      fromStr = fmt(today);
    } else if (preset === "week") {
      const start = new Date(today);
      start.setDate(today.getDate() - 7);
      fromStr = fmt(start);
    } else if (preset === "month") {
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      fromStr = fmt(start);
    }

    onFilterChange({ ...filters, from: fromStr, to: toStr });
  };

  const handleExportCSV = () => {
    if (!reports || reports.length === 0) return;

    let headers = [];
    let rows = [];

    if (reportType === "product") {
      headers = [
        "Product",
        "Category",
        "Base Unit",
        "Issued Base Qty",
        "Returned Base Qty",
        "POS Settled Qty",
        "Wastage/Usage Qty",
        "Reconciled Qty",
        "Net Outstanding Custody",
      ];
      rows = reports.map((r) => [
        `"${r.item_title}"`,
        `"${r.category || ""}"`,
        `"${r.unit || ""}"`,
        r.issued_base_qty || 0,
        r.returned_base_qty || 0,
        r.settled_base_qty || 0,
        r.consumed_base_qty || 0,
        r.adjusted_base_qty || 0,
        r.net_custody_base_qty || 0,
      ]);
    } else {
      headers = [
        "Custodian",
        "Department",
        "Active Items Count",
        "Total Issued Qty",
        "Total Returned Qty",
        "Total POS Sold",
        "Total Wastage",
        "Total Adjustments",
        "Net Outstanding Custody",
      ];
      rows = reports.map((r) => [
        `"${r.employee_name || r.user_id}"`,
        `"${r.department || ""}"`,
        r.item_count || 0,
        r.issued_base_qty || 0,
        r.returned_base_qty || 0,
        r.settled_base_qty || 0,
        r.consumed_base_qty || 0,
        r.adjusted_base_qty || 0,
        r.net_custody_base_qty || 0,
      ]);
    }

    const csvContent =
      "data:text/csv;charset=utf-8," +
      [headers.join(","), ...rows.map((e) => e.join(","))].join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute(
      "download",
      `item_issue_report_${reportType}_${new Date().toISOString().split("T")[0]}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="space-y-6">
      {/* Controls Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 bg-base-100 p-4 rounded-2xl border border-base-200 shadow-sm print:hidden">
        {/* Toggle Report Type */}
        <div className="join">
          <button
            onClick={() => handleTypeToggle("product")}
            className={`join-item btn btn-sm ${
              reportType === "product" ? "btn-primary" : "btn-outline"
            }`}
          >
            <IconBox size={16} stroke={iconStroke} />
            By Product
          </button>
          <button
            onClick={() => handleTypeToggle("custodian")}
            className={`join-item btn btn-sm ${
              reportType === "custodian" ? "btn-primary" : "btn-outline"
            }`}
          >
            <IconUsers size={16} stroke={iconStroke} />
            By Staff / Custodian
          </button>
        </div>

        {/* Date Filters & Presets */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1">
            <button
              onClick={() => handleDatePreset("today")}
              className="btn btn-xs btn-ghost text-xs"
            >
              Today
            </button>
            <button
              onClick={() => handleDatePreset("week")}
              className="btn btn-xs btn-ghost text-xs"
            >
              This Week
            </button>
            <button
              onClick={() => handleDatePreset("month")}
              className="btn btn-xs btn-ghost text-xs"
            >
              This Month
            </button>
          </div>

          <div className="flex items-center gap-1.5 text-xs">
            <input
              type="date"
              className="input input-sm input-bordered text-xs"
              value={filters.from || ""}
              onChange={(e) => onFilterChange({ ...filters, from: e.target.value })}
            />
            <span className="text-base-content/40 font-bold">to</span>
            <input
              type="date"
              className="input input-sm input-bordered text-xs"
              value={filters.to || ""}
              onChange={(e) => onFilterChange({ ...filters, to: e.target.value })}
            />
          </div>
        </div>

        {/* Export & Print Actions */}
        <div className="flex items-center gap-2">
          <button
            onClick={handleExportCSV}
            disabled={!reports || reports.length === 0}
            className="btn btn-sm btn-outline gap-1 text-xs"
          >
            <IconDownload size={16} stroke={iconStroke} />
            CSV
          </button>
          <button
            onClick={handlePrint}
            className="btn btn-sm btn-outline gap-1 text-xs"
          >
            <IconPrinter size={16} stroke={iconStroke} />
            Print
          </button>
        </div>
      </div>

      {/* Summary KPI Cards */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 print:hidden">
          <div className="bg-base-100 p-4 rounded-xl border border-base-200 shadow-sm">
            <span className="text-xs text-base-content/60 block uppercase tracking-wider font-semibold">
              Total Issued
            </span>
            <span className="text-xl font-bold text-primary mt-1 block">
              {Number(summary.total_issued_base_qty || 0).toLocaleString()} base
            </span>
          </div>

          <div className="bg-base-100 p-4 rounded-xl border border-base-200 shadow-sm">
            <span className="text-xs text-base-content/60 block uppercase tracking-wider font-semibold">
              Total Returned
            </span>
            <span className="text-xl font-bold text-success mt-1 block">
              {Number(summary.total_returned_base_qty || 0).toLocaleString()} base
            </span>
          </div>

          <div className="bg-base-100 p-4 rounded-xl border border-base-200 shadow-sm">
            <span className="text-xs text-base-content/60 block uppercase tracking-wider font-semibold">
              POS Settled Sales
            </span>
            <span className="text-xl font-bold text-info mt-1 block">
              {Number(summary.total_settled_base_qty || 0).toLocaleString()} base
            </span>
          </div>

          <div className="bg-base-100 p-4 rounded-xl border border-base-200 shadow-sm">
            <span className="text-xs text-base-content/60 block uppercase tracking-wider font-semibold">
              Outstanding Custody
            </span>
            <span className="text-xl font-bold text-warning mt-1 block">
              {Number(summary.total_net_custody_base_qty || 0).toLocaleString()} base
            </span>
          </div>
        </div>
      )}

      {/* Report Table */}
      <div className="bg-base-100 rounded-2xl border border-base-200 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-base-200 flex items-center justify-between">
          <h3 className="font-bold text-base-content text-sm">
            {reportType === "product"
              ? "Product Custody & Consumption Summary"
              : "Staff Custody & Performance Ledger"}
          </h3>
          <span className="text-xs text-base-content/50">
            {reports.length} records analyzed
          </span>
        </div>

        {isLoading ? (
          <div className="flex flex-col items-center justify-center p-12 text-gray-400">
            <IconRotate className="animate-spin mb-3" size={32} stroke={iconStroke} />
            <p className="text-xs">Generating report...</p>
          </div>
        ) : reports.length === 0 ? (
          <div className="text-center py-12 px-4">
            <IconFileSpreadsheet className="mx-auto text-base-content/30 mb-2" size={40} stroke={iconStroke} />
            <h4 className="font-bold text-base-content text-sm">No report data found</h4>
            <p className="text-xs text-base-content/60 max-w-sm mx-auto mt-1">
              No transactions match the selected date range.
            </p>
          </div>
        ) : reportType === "product" ? (
          <div className="overflow-x-auto">
            <table className="table table-sm w-full text-xs">
              <thead>
                <tr className="bg-base-200/50 text-base-content/70">
                  <th>Product</th>
                  <th>Category</th>
                  <th>Base Unit</th>
                  <th className="text-right">Issued</th>
                  <th className="text-right">Returned</th>
                  <th className="text-right">POS Sales</th>
                  <th className="text-right">Wastage</th>
                  <th className="text-right">Adjustments</th>
                  <th className="text-right font-bold text-primary">Outstanding Custody</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-base-200">
                {reports.map((r, i) => (
                  <tr key={i} className="hover:bg-base-200/40">
                    <td className="font-bold">{r.item_title}</td>
                    <td>{r.category || "General"}</td>
                    <td>{r.unit}</td>
                    <td className="text-right font-mono">{Number(r.issued_base_qty || 0).toLocaleString()}</td>
                    <td className="text-right font-mono text-success">
                      {Number(r.returned_base_qty || 0).toLocaleString()}
                    </td>
                    <td className="text-right font-mono text-info">
                      {Number(r.settled_base_qty || 0).toLocaleString()}
                    </td>
                    <td className="text-right font-mono text-warning">
                      {Number(r.consumed_base_qty || 0).toLocaleString()}
                    </td>
                    <td className="text-right font-mono text-secondary">
                      {Number(r.adjusted_base_qty || 0).toLocaleString()}
                    </td>
                    <td className="text-right font-mono font-bold text-primary">
                      {Number(r.net_custody_base_qty || 0).toLocaleString()} {r.unit}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="table table-sm w-full text-xs">
              <thead>
                <tr className="bg-base-200/50 text-base-content/70">
                  <th>Custodian</th>
                  <th>Department</th>
                  <th className="text-center">Items Held</th>
                  <th className="text-right">Issued</th>
                  <th className="text-right">Returned</th>
                  <th className="text-right">POS Sales</th>
                  <th className="text-right">Wastage</th>
                  <th className="text-right">Adjustments</th>
                  <th className="text-right font-bold text-primary">Outstanding Custody</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-base-200">
                {reports.map((r, i) => (
                  <tr key={i} className="hover:bg-base-200/40">
                    <td className="font-bold">{r.employee_name || r.user_id}</td>
                    <td>{r.department || "Staff"}</td>
                    <td className="text-center font-semibold">{r.item_count || 1}</td>
                    <td className="text-right font-mono">{Number(r.issued_base_qty || 0).toLocaleString()}</td>
                    <td className="text-right font-mono text-success">
                      {Number(r.returned_base_qty || 0).toLocaleString()}
                    </td>
                    <td className="text-right font-mono text-info">
                      {Number(r.settled_base_qty || 0).toLocaleString()}
                    </td>
                    <td className="text-right font-mono text-warning">
                      {Number(r.consumed_base_qty || 0).toLocaleString()}
                    </td>
                    <td className="text-right font-mono text-secondary">
                      {Number(r.adjusted_base_qty || 0).toLocaleString()}
                    </td>
                    <td className="text-right font-mono font-bold text-primary">
                      {Number(r.net_custody_base_qty || 0).toLocaleString()} base
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
